import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECT_META = {
  maths:   { emoji: '➗', label: 'Maths', color: 'text-amber-700' },
  english: { emoji: '📖', label: 'English', color: 'text-emerald-700' },
  science: { emoji: '🔬', label: 'Science', color: 'text-sky-700' },
};

const Bar = ({ value, max = 100 }) => (
  <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
    <div className="bg-primary h-full transition-all"
         style={{ width: `${Math.min(100, (value / max) * 100)}%` }} />
  </div>
);

export default function Progress() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { registration_id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!registration_id) return;
    axios.get(`${API}/quiz/progress/${registration_id}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.status === 404 ? 'not_found' : 'load_error'));
  }, [registration_id]);

  if (!registration_id) return <Navigate to="/register" replace />;
  if (error === 'not_found') return <div className="p-8">{t('progress.not_found') || 'Registration not found.'}</div>;
  if (!data) return <div className="p-8 text-sm text-muted-foreground">{t('common.loading') || 'Loading…'}</div>;

  return (
    <div className="min-h-screen bg-background px-4 py-8" data-testid="progress-page">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <p className="text-sm text-muted-foreground">{t('progress.hello') || 'Hello,'}</p>
          <h1 className="text-3xl font-bold" data-testid="student-name">
            {data.student_name} <span className="text-base font-normal text-muted-foreground">· Grade {data.grade}</span>
          </h1>
        </div>

        {/* Headline stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card data-testid="stat-lessons" className="gop-fade-in-up" style={{ animationDelay: '0ms' }}>
            <CardHeader className="pb-2"><CardDescription>{t('progress.stat.lessons') || 'Lessons done'}</CardDescription></CardHeader>
            <CardContent className="text-3xl font-bold">{data.total_lessons_completed}</CardContent>
          </Card>
          <Card data-testid="stat-score" className="gop-fade-in-up" style={{ animationDelay: '60ms' }}>
            <CardHeader className="pb-2"><CardDescription>{t('progress.stat.avg') || 'Avg score'}</CardDescription></CardHeader>
            <CardContent className="text-3xl font-bold">{Math.round(data.total_score)}%</CardContent>
          </Card>
          <Card data-testid="stat-streak" className="gop-fade-in-up" style={{ animationDelay: '120ms' }}>
            <CardHeader className="pb-2"><CardDescription>{t('progress.stat.streak') || 'Streak'}</CardDescription></CardHeader>
            <CardContent className="text-3xl font-bold">🔥 {data.streak_days}</CardContent>
          </Card>
          <Card data-testid="stat-percentile" className="gop-fade-in-up" style={{ animationDelay: '180ms' }}>
            <CardHeader className="pb-2"><CardDescription>{t('progress.stat.top') || 'Top 10%'}</CardDescription></CardHeader>
            <CardContent className="text-sm text-muted-foreground italic pt-4">{data.top_percentile_status}</CardContent>
          </Card>
        </div>

        {/* Per-subject */}
        <div>
          <h2 className="text-xl font-bold mb-3">{t('progress.subjects') || 'Subjects'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {data.subjects.map(s => {
              const m = SUBJECT_META[s.subject] || {};
              return (
                <Card key={s.subject} data-testid={`subject-card-${s.subject}`}>
                  <CardHeader>
                    <CardTitle className={`flex items-center gap-2 ${m.color}`}>
                      <span>{m.emoji}</span>
                      <span>{t(`progress.subject.${s.subject}`) || m.label || s.subject}</span>
                    </CardTitle>
                    <CardDescription>
                      {t('progress.subject.completed_of', { done: s.completed_lessons, total: s.total_lessons }) ||
                        `${s.completed_lessons} of ${s.total_lessons} lessons`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Bar value={s.completed_lessons} max={s.total_lessons} />
                    <p className="text-sm">{t('progress.subject.avg') || 'Avg:'} <strong>{Math.round(s.average_score)}%</strong></p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Badges */}
        <div>
          <h2 className="text-xl font-bold mb-3">{t('progress.badges') || 'Badges'}</h2>
          {data.badges.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('progress.badges.empty') || 'No badges yet — complete a quiz to unlock your first one.'}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="badges-wall">
              {data.badges.map(b => <Badge key={b} className="text-base py-1 px-3">🏅 {b}</Badge>)}
            </div>
          )}
        </div>

        <Button onClick={() => navigate('/lessons')} data-testid="progress-back-lessons-btn" className="w-full">
          {t('progress.cta') || 'Take another lesson →'}
        </Button>
        <Button asChild variant="outline" className="w-full" data-testid="progress-id-card-btn">
          <a href={`/id-card/${registration_id}`}>🪪 My ID card →</a>
        </Button>
      </div>
    </div>
  );
}
